import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {timeUtil} from '@/helper/time';
import {minus, numUtil} from '@/helper/num';
import {orderUtil} from '@/helper/order';
import {getQtyTypePriceByOrderType, qtyUtil} from '@/helper/qty-type-contract';
import {contracts$} from '@/config';
import {calcValue} from '@/calc';
import {isTrailingStopOrder} from '@/tpslts/order-filters';
import {contractConditionalOrdersCache$} from './order-conditional-contract-cache';
import {contractConditionalOrdersAllCache$} from './order-conditional-contract-all-cache';
import type {ws, ContractConditionalOrder, PegPriceType, ContractPair} from '@/types';
type ContractOrderRaw = ws.ContractOrderRaw;

export type Cache = Map<string, ContractOrderRaw>;

export const contractConditionalOrders$ = combineLatest([contractConditionalOrdersCache$, contracts$]).pipe<
  ContractConditionalOrder[]
>(map(([cache, contracts]) => cache.toArray((o) => convertToConditionalOrder(o, contracts)).filter(Boolean)));

export const contractConditionalOrdersAll$ = combineLatest([contractConditionalOrdersAllCache$, contracts$]).pipe<
  ContractConditionalOrder[]
>(map(([cache, contracts]) => cache.toArray((o) => convertToConditionalOrder(o, contracts)).filter(Boolean)));

function direction(d, pegPriceType) {
  if (['TrailingTakeProfitPeg', 'TrailingTakeProfitByProportionPeg'].includes(pegPriceType)) {
    if (d === 'Rising') {
      return '≤';
    }
    if (d === 'Falling') {
      return '≥';
    }
  }
  if (d === 'Rising') {
    return '≥';
  }
  if (d === 'Falling') {
    return '≤';
  }
  return '';
}

function calcTriggerType(trigger) {
  if (trigger === 'ByLastPrice' || trigger === 'ByLastPriceLimit') {
    return 'LP';
  }
  if (trigger === 'ByMarkPrice' || trigger === 'ByMarkPriceLimit') {
    return 'MP';
  }
  return '';
}

function genStatus(ordStatus, pegPriceType) {
  if (
    ['TrailingTakeProfitPeg', 'TrailingTakeProfitByProportionPeg'].includes(pegPriceType) &&
    ordStatus === 'Untriggered'
  ) {
    return 'Inactive';
  }
  return ordStatus;
}

function calcActivationPriceEp(pegPriceType: PegPriceType, stopPxEp: number, pegOffsetValueEp: number): number {
  if (['TrailingTakeProfitPeg', 'TrailingTakeProfitByProportionPeg'].includes(pegPriceType)) {
    return stopPxEp - pegOffsetValueEp;
  }
  return 0;
}

function convertToConditionalOrder(
  raw: ContractOrderRaw,
  contracts: ContractPair[]
): ContractConditionalOrder & {
  displayQty: string;
  icebergOrderQtyByCont: string;
  icebergOrderQtyByValue: string;
  icebergOrderQtyByCoin: string;
} {
  const contract = contracts.find((o) => o.symbol === raw.symbol);
  if (!contract) {
    return null;
  }

  const {displaySymbol, pricePrecision, priceScale, contractSide, contractSize, priceFactor, valuePrecision} = contract;
  const {
    orderID,
    clOrdID,
    ordType,
    ordStatus,
    symbol,
    side,
    orderQty,
    priceEp,
    // @ts-ignore
    trigger,
    // @ts-ignore
    stopPxEp,
    // @ts-ignore
    actionTimeNs,
    // @ts-ignore
    stopDirection,
    // @ts-ignore
    timeInForce,
    // @ts-ignore
    pegOffsetValueEp,
    // @ts-ignore
    pegOffsetProportionEr,
    // @ts-ignore
    pegPriceType,
    // @ts-ignore
    takeProfitEp,
    // @ts-ignore
    stopLossEp,
    // @ts-ignore
    tpTrigger,
    // @ts-ignore
    slTrigger,
    // @ts-ignore
    curPosTerm,
    posSide,
    actionBy,
    // @ts-ignore
    displayQty,
    // @ts-ignore
    execInst,
    orderListStatus,
    orderListType,
    orderListId,
    userID,
  } = raw;
  const actionTimestamp = Math.floor(actionTimeNs / 1e6);
  const activationPriceEp = calcActivationPriceEp(pegPriceType, stopPxEp, pegOffsetValueEp);
  const orderQtyStr = orderUtil.formatOrderQty(orderQty);
  const price = numUtil.floor(priceEp, pricePrecision, priceScale);
  const qtyPrice = numUtil.floor(getQtyTypePriceByOrderType(raw, ordType), pricePrecision, priceScale);
  const isV2 = contract.type === 'PerpetualV2';
  const pegOffsetProportion = numUtil.floor(Math.abs(pegOffsetProportionEr), 2, isV2 ? -2 : 6) + '%';
  const isMarket = orderUtil.isMarketOrder(ordType);
  const priceEp2 = isMarket ? stopPxEp : priceEp;
  const value = calcValue(contractSide, contractSize, priceFactor, valuePrecision, orderQty, priceEp2);
  const curStopPxEp = pegPriceType === 'TrailingTakeProfitPeg' ? Number(minus(stopPxEp, pegOffsetValueEp)) : stopPxEp;
  return {
    orderID,
    clOrdID,
    ordType,
    displayOrdType: isTrailingStopOrder(pegPriceType, ordType) ? 'TrailingStop' : ordType,
    ordStatus: genStatus(ordStatus, pegPriceType),
    displaySymbol,
    symbol,
    side,
    orderQty: Number(orderQty),
    orderQtyByCont: qtyUtil.toCont(contract, orderQty),
    orderQtyByValue: qtyUtil.toValue(contract, orderQty, qtyPrice),
    orderQtyByCoin: qtyUtil.toCoin(contract, orderQty, qtyPrice),
    orderQtyStr,
    priceEp: Number(priceEp),
    price: numUtil.delimit(price),
    activationPriceEp,
    activationPrice: activationPriceEp ? numUtil.floor(activationPriceEp, pricePrecision, priceScale) : '-',
    stopPxEp,
    stopPx: numUtil.delimit(numUtil.floor(stopPxEp, pricePrecision, priceScale)),
    actionTimestamp,
    actionTime: timeUtil.formatDateTime(actionTimestamp),
    actionMonthDayTime: timeUtil.formatMonthDayTime(actionTimestamp),
    // @ts-ignore
    stopDirection: direction(stopDirection, pegPriceType),
    trigger,
    triggerType: calcTriggerType(trigger),
    timeInForce,
    pegOffsetValueEp,
    pegOffsetValue: numUtil.floor(Math.abs(pegOffsetValueEp), pricePrecision, priceScale),
    pegOffsetProportionEr,
    pegOffsetProportion,
    pegPriceType,
    removable: orderUtil.isFinalOrder(ordStatus),
    takeProfitEp,
    takeProfitPrice: numUtil.delimit(numUtil.floor(takeProfitEp, pricePrecision, priceScale)),
    stopLossEp,
    stopLossPrice: numUtil.delimit(numUtil.floor(stopLossEp, pricePrecision, priceScale)),
    tpTrigger,
    slTrigger,
    curPosTerm,
    posSide,
    actionBy,
    execInst,
    displayQty,
    icebergOrderQtyByCont: qtyUtil.toCont(contract, displayQty ?? 0),
    icebergOrderQtyByValue: qtyUtil.toValue(contract, displayQty ?? 0, qtyPrice),
    icebergOrderQtyByCoin: qtyUtil.toCoin(contract, displayQty ?? 0, qtyPrice),
    value,
    curStopPxEp,
    curStopPx: numUtil.floor(curStopPxEp, pricePrecision, priceScale),
    orderListStatus,
    orderListType,
    orderListId,
    userID,
  };
}
